import {Injectable, Optional} from '@angular/core';
import {Observable, throwError} from 'rxjs';
import {catchError, map, switchMap} from 'rxjs/operators';
import {Address, Basket, DeliveryMethod, Order, Payment, PaymentInfo} from '@kion/data-models';
import {HttpClient, HttpParams} from '@angular/common/http';
import {ApiConfig} from '../../../../../api-endpoint/src/lib/api.config';

interface IResponseItem {
  entries: any[],
  appliedVouchers: any[],
  totalPrice: Price,
  totalPriceWithTax: Price
  totalTax: Price,
  totalDiscounts: Price,
  deliveryAddress?: Address,
  paymentAddress?: Address,
  user: {
    name: string,
    uid?: string
  }
  status: string,
  code: string
  guid: string
  paymentInfo: any
  deliveryCost: Price
  completeDelivery?: boolean
}

interface IResponseItemOrder extends IResponseItem {
  concardisPaymentProviderRedirectUrl: string
}

interface IResponseItemDelivery {
  code: string,
  deliveryCost: Price
  name: string,
  note?: string,
  description: string
}

interface IResponseItemPayment {
  code: string,
  description: string,
  name: string,
  paymentConfiguration: {
    confirmationUrl: string,
    customStyleId: string,
    merchantId: string,
    returnUrl: string,
    successUrl: string
  }
}

interface Price {
  currencyIso: string
  formattedValue: string
  priceType: string
  value: number
}

interface SearchResponseDelivery {
  deliveryModes: IResponseItemDelivery[]
}

interface SearchResponsePayment {
  paymentModes: IResponseItemPayment[]
}

@Injectable({
  providedIn: 'root'
})
export class BasketConnectors {
  
  private readonly _rootUrl: string;
  private readonly _baseSite: string;

  private route = '';

  constructor(
    @Optional() apiConfig: ApiConfig,
    private http: HttpClient,
  ) {
    this._rootUrl = apiConfig.rootUrl;
    this._baseSite = apiConfig.baseSite;
  }

  private static transformFromApi(response: IResponseItem): Basket {
    return {
      ...response,
      id: response.guid,
      paymentInfo: response.paymentInfo || null
    };
  }

  private static transformOrderFromApi(response: IResponseItemOrder): Order {
    return {
      ...this.transformFromApi(response),
      redirectUrl: response.concardisPaymentProviderRedirectUrl
    }
  }

  private static transformDeliveryFromApi(response: IResponseItemDelivery): DeliveryMethod {
    return {
      ...response
    };
  }

  private static transformPaymentFromApi(response: IResponseItemPayment): Payment {
    return {
      ...response
    };
  }

  createOrFindBasket(basketId?: string, userId?: string): Observable<Basket> {
    const user = userId || 'anonymous';
    const route = this._rootUrl + this._baseSite + '/users/' + user + '/carts';
    if (basketId) {
      return this.getBasketForUser(basketId, userId).pipe(
        catchError(
          () => this.http.post<IResponseItem>(route, {}).pipe(
            map(
              (response: IResponseItem) => BasketConnectors.transformFromApi(response)
            ),
            catchError(error => throwError(error))
          )
        )
      )
    }
    return this.http.post<IResponseItem>(route, {}).pipe(
      map(
        (response: IResponseItem) => BasketConnectors.transformFromApi(response)
      ),
      catchError(error => throwError(error))
    );
  }

  getBasketForUser(basketId: string, userId?: string): Observable<Basket> {
    const user = userId || 'anonymous';
    const route = this._rootUrl + this._baseSite + '/users/' + user + '/carts/' + basketId;
    return this.http.get<IResponseItem>(route).pipe(
      map(
        (response: IResponseItem) => BasketConnectors.transformFromApi(response)
      ),
      catchError(error => throwError(error))
    );
  }

  addToBasket(basketId: string, productCode: string, qty: number, options: { code: string }[] = [], userId?: string): Observable<Basket> {
	  return this.addToBasketWithUnit(basketId, productCode, qty, null, options, userId);
  }

  addToBasketWithUnit(basketId: string, productCode: string, qty: number, unitIso?: string, options: { code: string }[] = [], userId?: string): Observable<Basket> {
    const user = userId || 'anonymous';
    const request = {
      product: {
        code: productCode
      },
      quantity: qty,
	      unitIso: unitIso,
      productOptions: options
    };
    const route = this._rootUrl + this._baseSite + '/users/' + user + '/carts/' + basketId + '/entries';
    return this.http.post<IResponseItem>(route, request).pipe(
      switchMap(
        () => this.getBasketForUser(basketId, userId)
      ),
      catchError(error => throwError(error))
    );
  }

  loadDeliveryData(basketId: string, userId?: string): Observable<DeliveryMethod[]> {
    const user = userId || 'anonymous';
    const route = this._rootUrl + this._baseSite + '/users/' + user + '/carts/' + basketId + '/deliverymodes';
    return this.http.get<SearchResponseDelivery>(route).pipe(
      map(
        (response: SearchResponseDelivery) => response.deliveryModes.map(
          (result: IResponseItemDelivery) => BasketConnectors.transformDeliveryFromApi(result)
        )
      ),
      catchError(error => throwError(error))
    )
  }

  setDeliveryData(basketId: string, deliveryMethod: DeliveryMethod, userId?: string): Observable<Basket> {
    const user = userId || 'anonymous';
    const route = this._rootUrl + this._baseSite + '/users/' + user + '/carts/' + basketId + '/deliverymode?deliveryModeId=' + deliveryMethod.code;
    const routeNote = this._rootUrl + this._baseSite + '/users/' + user + '/carts/' + basketId + '/deliverynotice';
    return this.http.put<void>(route, {}).pipe(
      switchMap(
        () => this.http.post<void>(routeNote, {deliveryNotice: deliveryMethod.note}).pipe(
          switchMap(
            () => this.getBasketForUser(basketId, userId)
          )
        )
      ),
      catchError(error => throwError(error))
    )
  }

  addEmailToBasket(basketId: string, email: string, userId?: string): Observable<Basket> {
    const user = userId || 'anonymous';
    const route = this._rootUrl + this._baseSite + '/users/' + user + '/carts/' + basketId + '/email' + '?email=' + email;
    return this.http.put<void>(route, {}).pipe(
      switchMap(
        () => this.getBasketForUser(basketId, userId)
      ),
      catchError(
        error => throwError(error)
      )
    )
  }

  loadPaymentMethods(): Observable<Payment[]> {
    const route = this._rootUrl + this._baseSite + '/paymentmodes/active';
    return this.http.get<SearchResponsePayment>(route).pipe(
      map(
        (response: SearchResponsePayment) => response?.paymentModes.map(
          (result: IResponseItemPayment) => BasketConnectors.transformPaymentFromApi(result)
        )
      ),
      catchError(error => throwError(error))
    )
  }

  loadSupportedPaymentMethods(): Observable<Payment[]> {
    const route = this._rootUrl + this._baseSite + '/dm/paymentmodes/active';
    return this.http.get<SearchResponsePayment>(route).pipe(
      map(
        (response: SearchResponsePayment) => response?.paymentModes.map(
          (result: IResponseItemPayment) => BasketConnectors.transformPaymentFromApi(result)
        )
      ),
      catchError(error => throwError(error))
    )
  }

  setPayment(basketId: string, payment: Partial<PaymentInfo>, userId?: string): Observable<Basket> {
    const user = userId || 'anonymous';
    const route = this._rootUrl + this._baseSite + '/users/' + user + '/carts/' + basketId + '/paymentinfos';
    return this.http.post<void>(route, payment).pipe(
      switchMap(
        () => this.getBasketForUser(basketId, userId)
      ),
      catchError(error => throwError(error))
    );
  }

  placeOrder(basketId: string, userId?: string): Observable<Order> {
    const user = userId || 'anonymous';
    const route = this._rootUrl + this._baseSite + '/users/' + user + '/orders' + '?cartId=' + basketId;
    return this.http.post<any>(route, {}).pipe(
      map(
        (response) => {
          return BasketConnectors.transformOrderFromApi(response);
        }
      ),
      catchError(error => throwError(error))
    )
  }

  finalizePayment(basketId: string, transactionId: string, userId?: string): Observable<Order> {
    const user = userId || 'anonymous';
    const route = this._rootUrl + this._baseSite + '/users/' + user + '/carts/' + basketId + '/finalizePayment?transactionId=' + transactionId;
    return this.http.post<any>(route, {}).pipe(
      map(
        (order) => BasketConnectors.transformOrderFromApi(order)
      ),
      catchError(error => throwError(error))
    )
  }

  changeQuantity(basketId: string, entryNumber: string, quantity: number): Observable<Basket> {
    const route = this._rootUrl + this._baseSite + '/users/anonymous' + '/carts/' + basketId + '/entries/' + entryNumber;
    const request = {
      quantity: quantity,
    };
    return this.http.patch<void>(route, request).pipe(
      switchMap(
        () => this.getBasketForUser(basketId)
      ),
      catchError(error => throwError(error))
    )
  }

  deleteEntry(basketId: string, entryNumber: string): Observable<Basket> {
    const route = this._rootUrl + this._baseSite + '/users/anonymous' + '/carts/' + basketId + '/entries/' + entryNumber;
    return this.http.delete<void>(route).pipe(
      switchMap(
        () => this.getBasketForUser(basketId)
      ),
      catchError(error => throwError(error))
    )
  }

  transformAnonymousToPersonal(userId: string, oldBasketId: string): Observable<Basket> {
    const route = this.getTransformAnonymousToPersonalUrl(userId, oldBasketId);
    return this.http.post<IResponseItem>(route, {}).pipe(
      map(
        (response: IResponseItem) => BasketConnectors.transformFromApi(response)
      ),
      catchError(error => throwError(error))
    );
  }

  addUserReference(basketId: string, reference: string): Observable<Basket> {
    const route = this._rootUrl + this._baseSite + '/users/anonymous/carts/' + basketId + '/userreference';
    return this.http.post<void>(route, {userReference: reference}).pipe(
      switchMap(
        () => this.getBasketForUser(basketId)
      )
    )
  }

  setAddress(addressId: string, basketId: string, isDelivery: boolean): Observable<Basket> {
    let extension = 'payment';
    if (isDelivery) {
      extension = 'delivery';
    }
    const route = this._rootUrl + this._baseSite + '/users/anonymous/carts/' + basketId + '/addresses/' + extension + '?addressId=' + addressId;
    return this.http.put<void>(route, {}).pipe(
      switchMap(
        () => this.getBasketForUser(basketId)
      ),
      catchError(error => throwError(error))
    )
  }

  addPromotion(basketId: string, promotionCode: string): Observable<Basket> {
    const route = this._rootUrl + this._baseSite + '/users/anonymous/carts/' + basketId + '/vouchers?voucherId=' + promotionCode;
    return this.http.post<void>(route, {}).pipe(
      switchMap(
        () => this.getBasketForUser(basketId)
      ),
      catchError(error => throwError(error))
    );
  }

  deletePromotionCode(basketId: string, promotionCode: string): Observable<Basket> {
    const route = this._rootUrl + this._baseSite + '/users/anonymous/carts/' + basketId + '/vouchers/' + promotionCode;
    return this.http.delete<void>(route, {}).pipe(
      switchMap(
        () => this.getBasketForUser(basketId)
      ),
      catchError(error => throwError(error))
    );
  }

  updatePurchaseOrderNumber(basketId: string, purchaseOrderNumber: string): Observable<Basket> {
    return this.updatePartialBasket(basketId, {purchaseOrderNumber: purchaseOrderNumber});
  }

  applyPostalCodeMapping(basketId: string): Observable<void> {
    const route = `${this._rootUrl}${this._baseSite}/users/anonymous/carts/${basketId}/postalcodemapping/`;
    return this.http.post(route, {}).pipe(
      map(
        () => null
      ),
      catchError(error => throwError(error))
    )
  }

  setCompleteDelivery(userId: string, basketId: string, completeDelivery: boolean) {
    const user = userId || 'anonymous';
    const route = this._rootUrl + this._baseSite + '/users/' + user + '/carts/'+basketId+'/setCompleteDelivery/';

    let params: HttpParams = new HttpParams()
    .set("completeDelivery", completeDelivery);
  
    return this.http.post(route, null,  {params}).pipe(
      map(
        () => null
      ),
      catchError(error => throwError(error))
    )
  }


  protected getManyUrl() {
    return this._rootUrl + this.route;
  }

  protected getOneUrl(id: number) {
    return this._rootUrl + this.route + id;
  }

  protected getTransformAnonymousToPersonalUrl(userId: string, oldBasketId: string): string {
    const user = userId || 'anonymous';
    return this._rootUrl + this._baseSite + '/users/' + user + '/carts/?oldCartId=' + oldBasketId;
  }

  private updatePartialBasket(basketId: string, request: object): Observable<Basket> {
    const url = this._rootUrl + this._baseSite + '/users/anonymous/carts/' + basketId;
    return this.http.post<void>(url, request).pipe(
      switchMap(
        () => this.getBasketForUser(basketId)
      ),
      catchError(error => throwError(error))
    )
  }
}
